<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useTrack } from 'dashboard/composables';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import BotMetrics from './components/BotMetrics.vue';
import BotAiInsights from './components/BotAiInsights.vue';
import BotUnresolvedQuestions from './components/BotUnresolvedQuestions.vue';
import BotImproveBacklog from './components/BotImproveBacklog.vue';
import ReportContainer from './ReportContainer.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();
const store = useStore();

const since = ref(0);
const until = ref(0);
const groupBy = ref(GROUP_BY_FILTER[1]);

const REPORT_KEYS = {
  BOT_RESOLUTION_COUNT: 'bot_resolutions_count',
  BOT_HANDOFF_COUNT: 'bot_handoffs_count',
};

const accountReport = useMapGetter('getAccountReports');
const botSummary = useMapGetter('getBotSummary');
const botSummaryStatus = useMapGetter('getBotSummaryFetchingStatus');
const errorMessage = ref('');
const latestRefreshToken = ref(0);

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = refreshToken =>
  refreshToken === latestRefreshToken.value;

const requestPayload = computed(() => ({ from: since.value, to: until.value }));
const isBotSummaryLoading = computed(
  () => botSummaryStatus.value === 'fetching'
);

const kpiResolutions = computed(() =>
  Number(botSummary.value?.bot_resolutions_count || 0)
);
const kpiPrevResolutions = computed(() =>
  Number(botSummary.value?.previous?.bot_resolutions_count || 0)
);
const kpiHandoffs = computed(() =>
  Number(botSummary.value?.bot_handoffs_count || 0)
);
const kpiPrevHandoffs = computed(() =>
  Number(botSummary.value?.previous?.bot_handoffs_count || 0)
);

const kpiResolutionDiff = computed(() => {
  if (!kpiPrevResolutions.value) return 0;
  return Math.round(
    ((kpiResolutions.value - kpiPrevResolutions.value) /
      kpiPrevResolutions.value) *
      100
  );
});

const kpiHandoffDiff = computed(() => {
  if (!kpiPrevHandoffs.value) return 0;
  return Math.round(
    ((kpiHandoffs.value - kpiPrevHandoffs.value) / kpiPrevHandoffs.value) * 100
  );
});

const trendLoading = computed(() =>
  Boolean(
    accountReport.value?.isFetching?.bot_resolutions_count ||
      accountReport.value?.isFetching?.bot_handoffs_count
  )
);

const hasTrendData = computed(() => {
  const a = accountReport.value?.data?.bot_resolutions_count || [];
  const b = accountReport.value?.data?.bot_handoffs_count || [];
  return a.length > 0 || b.length > 0;
});

const formatTrendLabel = timestamp => {
  if (!timestamp) return '--';
  const d = fromUnixTime(timestamp);
  const period = groupBy.value?.period;
  if (period === GROUP_BY_FILTER[2].period) return format(d, 'dd-MMM');
  if (period === GROUP_BY_FILTER[3].period) return format(d, 'MMM-yyyy');
  if (period === GROUP_BY_FILTER[4].period) return format(d, 'yyyy');
  return format(d, 'dd-MMM');
};

const trendCollection = computed(() => {
  const resolutions = accountReport.value?.data?.bot_resolutions_count || [];
  const handoffs = accountReport.value?.data?.bot_handoffs_count || [];
  const base = resolutions.length ? resolutions : handoffs;
  const labels = (base || []).map(p => formatTrendLabel(p.timestamp));
  return {
    labels,
    datasets: [
      {
        label: t('REPORT.METRICS.BOT_RESOLUTION_COUNT.NAME'),
        data: labels.map((_, idx) => Number(resolutions?.[idx]?.value || 0)),
        borderColor: '#0076f5',
        backgroundColor: '#0076f5',
        pointRadius: 2,
        pointHoverRadius: 3,
        tension: 0.28,
        borderWidth: 2,
        fill: false,
      },
      {
        label: t('REPORT.METRICS.BOT_HANDOFF_COUNT.NAME'),
        data: labels.map((_, idx) => Number(handoffs?.[idx]?.value || 0)),
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        pointRadius: 2,
        pointHoverRadius: 3,
        tension: 0.28,
        borderWidth: 2,
        fill: false,
      },
    ],
  };
});

const trendOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  plugins: { legend: { display: true } },
  interaction: { mode: 'nearest', intersect: false },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#6b7280' } },
    y: {
      beginAtZero: true,
      grid: { color: '#e5e7eb' },
      ticks: { color: '#6b7280', callback: v => Number(v).toLocaleString() },
    },
  },
};

const getPayload = () => ({
  from: since.value,
  to: until.value,
  groupBy: groupBy.value?.period,
  businessHours: false,
  wlabAiOnly: true,
});

const fetchBotSummary = () => store.dispatch('fetchBotSummary', getPayload());

const fetchChartData = async () => {
  const results = await Promise.all(
    Object.values(REPORT_KEYS).map(metric =>
      store.dispatch('fetchAccountReport', {
        metric,
        ...getPayload(),
      })
    )
  );
  return results.every(Boolean);
};

const fetchAllData = async () => {
  if (!since.value || !until.value) return;
  const refreshToken = createRefreshToken();
  errorMessage.value = '';

  const [isSummaryOk, isChartOk] = await Promise.all([
    fetchBotSummary(),
    fetchChartData(),
  ]);

  if (!isLatestRefresh(refreshToken)) return;

  if (!isSummaryOk || !isChartOk) {
    errorMessage.value = t('REPORT.DATA_FETCHING_FAILED');
  }
};

const onFilterChange = ({ from, to }) => {
  since.value = from;
  until.value = to;
  fetchAllData();
  useTrack(REPORTS_EVENTS.FILTER_REPORT, {
    filterValue: { from, to },
    reportType: 'wlab_ai',
  });
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="br-page">
    <div class="br-shell">
      <div class="br-header">
        <div class="br-header-left">
          <h1 class="br-title">{{ $t('BOT_REPORTS.HEADER') }}</h1>
        </div>
      </div>

      <div class="br-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          :show-inbox="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="br-content">
        <p v-if="errorMessage" class="br-error">
          {{ errorMessage }}
        </p>

        <div class="br-cards">
          <div class="br-card">
            <div class="br-card-label">
              {{ $t('REPORT.METRICS.BOT_RESOLUTION_COUNT.NAME') }}
            </div>
            <div class="br-card-main">
              <span class="br-card-value">
                {{
                  isBotSummaryLoading ? '—' : kpiResolutions.toLocaleString()
                }}
              </span>
              <span
                v-if="
                  !isBotSummaryLoading &&
                  (kpiPrevResolutions || kpiResolutionDiff !== 0)
                "
                class="br-pill"
                :class="
                  kpiResolutionDiff < 0 ? 'br-pill--red' : 'br-pill--green'
                "
              >
                <svg
                  v-if="kpiResolutionDiff < 0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 19V5m0 14 4-4m-4 4-4-4"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 5v14m0-14L8 9m4-4 4 4"
                  />
                </svg>
                {{ Math.abs(kpiResolutionDiff) }}%
              </span>
            </div>
          </div>

          <div class="br-card">
            <div class="br-card-label">
              {{ $t('REPORT.METRICS.BOT_HANDOFF_COUNT.NAME') }}
            </div>
            <div class="br-card-main">
              <span class="br-card-value">
                {{ isBotSummaryLoading ? '—' : kpiHandoffs.toLocaleString() }}
              </span>
              <span
                v-if="
                  !isBotSummaryLoading &&
                  (kpiPrevHandoffs || kpiHandoffDiff !== 0)
                "
                class="br-pill"
                :class="kpiHandoffDiff < 0 ? 'br-pill--red' : 'br-pill--green'"
              >
                <svg
                  v-if="kpiHandoffDiff < 0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 19V5m0 14 4-4m-4 4-4-4"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 5v14m0-14L8 9m4-4 4 4"
                  />
                </svg>
                {{ Math.abs(kpiHandoffDiff) }}%
              </span>
            </div>
          </div>
        </div>

        <section class="br-card-section">
          <div class="br-card-section-header">
            <div class="br-card-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 3v18h18M7 14l3-3 3 2 4-5"
                />
              </svg>
              <span>Bot resolution &amp; handoff trend</span>
            </div>
          </div>
          <div class="br-chart">
            <woot-loading-state
              v-if="trendLoading"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!hasTrendData" class="br-chart-empty">
              {{ $t('REPORT.NO_ENOUGH_DATA') }}
            </div>
            <LineChart
              v-else
              :collection="trendCollection"
              :chart-options="trendOptions"
            />
          </div>
        </section>

        <section class="br-card-section">
          <div class="br-card-section-header">
            <div class="br-card-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <span>AI Metrics</span>
            </div>
          </div>
          <div class="br-sub-body">
            <BotMetrics :filters="requestPayload" />
          </div>
        </section>

        <section class="br-card-section">
          <div class="br-card-section-header">
            <div class="br-card-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m1.343-5.657-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <span>{{ $t('BOT_REPORTS.INSIGHTS.HEADER') }}</span>
            </div>
          </div>
          <div class="br-sub-body">
            <BotAiInsights :filters="requestPayload" />
          </div>
        </section>

        <section class="br-card-section">
          <div class="br-card-section-header">
            <div class="br-card-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
                />
              </svg>
              <span>{{ $t('BOT_REPORTS.UNRESOLVED.HEADER') }}</span>
            </div>
          </div>
          <div class="br-sub-body br-sub-body--padded">
            <BotUnresolvedQuestions :filters="requestPayload" />
          </div>
        </section>

        <section class="br-card-section">
          <div class="br-card-section-header">
            <div class="br-card-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2"
                />
              </svg>
              <span>{{ $t('BOT_REPORTS.IMPROVE_BACKLOG.HEADER') }}</span>
            </div>
          </div>
          <div class="br-sub-body br-sub-body--padded">
            <BotImproveBacklog :filters="requestPayload" />
          </div>
        </section>

        <section class="br-card-section">
          <div class="br-card-section-header">
            <div class="br-card-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 21H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M7 3v4M17 3v4M3 9h18"
                />
              </svg>
              <span>Historical data</span>
            </div>
          </div>
          <div class="br-sub-body">
            <ReportContainer
              account-summary-key="getBotSummary"
              summary-fetching-key="getBotSummaryFetchingStatus"
              :group-by="groupBy"
              :report-keys="REPORT_KEYS"
            />
          </div>
        </section>

        <p class="br-footer">All reports are displayed in your local time.</p>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.br-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.br-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.br-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.br-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.br-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.br-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.br-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* ── Force light-mode CSS variables so subcomponents look right ── */
  --n-slate-12: #111827;
  --n-slate-11: #374151;
  --n-slate-10: #6b7280;
  --n-slate-9: #9ca3af;
  --n-slate-8: #d1d5db;
  --n-slate-7: #e5e7eb;
  --n-solid-1: #ffffff;
  --n-solid-2: #f9fafb;
  --n-solid-3: #f3f4f6;
  --n-solid-4: #e5e7eb;
  --n-container: rgba(0, 0, 0, 0.08);
  --n-container-2: rgba(0, 0, 0, 0.12);
  --n-brand: #1d75e5;
  --n-weak: rgba(0, 0, 0, 0.06);
}

/* ── KPI cards ── */
.br-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.br-card {
  display: flex;
  flex-direction: column;
  min-height: 110px;
  padding: 14px 16px 12px;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  background: #f3f4f6;
}

.br-card-label {
  font-size: 12px;
  line-height: 1.3;
  color: #374151;
  font-weight: 600;
}

.br-card-main {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
}

.br-card-value {
  font-size: 32px;
  line-height: 1.05;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.03em;
}

.br-pill {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 6px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.br-pill--green {
  color: #157f3f;
  background: #e8f7ed;
}
.br-pill--red {
  color: #b42318;
  background: #fdebec;
}

/* ── Section cards ── */
.br-card-section {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.br-card-section-header {
  min-height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.br-card-section-title {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ── Chart ── */
.br-chart {
  position: relative;
  height: 260px;
  margin: 0 16px 8px;
}

.br-chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.br-error {
  margin: 0;
  color: #b42318;
  font-size: 13px;
  font-weight: 500;
}

/* ── Sub-component body ── */
.br-sub-body {
  /* Remove subcomponents' own outer card styling so content is seamless */
}

.br-sub-body--padded {
  padding: 0 16px 16px;
}

/* ── Subcomponent overrides: force light-mode colours ── */
.br-content :deep(.bg-n-solid-2) {
  background: #f9fafb !important;
}
.br-content :deep(.bg-n-solid-3) {
  background: #f3f4f6 !important;
}
.br-content :deep(.outline-n-container) {
  outline-color: #e2e8f0 !important;
}
.br-content :deep(.text-n-slate-12) {
  color: #111827 !important;
}
.br-content :deep(.text-n-slate-11) {
  color: #374151 !important;
}
.br-content :deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
.br-content :deep(.text-n-slate-9) {
  color: #9ca3af !important;
}
.br-content :deep(.text-slate-800) {
  color: #111827 !important;
}
.br-content :deep(.text-slate-600) {
  color: #6b7280 !important;
}
.br-content :deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
.br-content :deep(.border-n-strong) {
  border-color: #d1d5db !important;
}

/* Tailwind dark: utility overrides */
.br-content :deep(.dark\:text-white) {
  color: #111827 !important;
}
.br-content :deep(.dark\:text-white\/90) {
  color: #111827 !important;
}
.br-content :deep(.dark\:text-white\/60) {
  color: #6b7280 !important;
}
.br-content :deep(.dark\:text-white\/40) {
  color: #9ca3af !important;
}
.br-content :deep(.dark\:bg-slate-900) {
  background: #ffffff !important;
}
.br-content :deep(.dark\:bg-slate-800) {
  background: #f9fafb !important;
}
.br-content :deep(.dark\:border-slate-700) {
  border-color: #e5e7eb !important;
}
.br-content :deep(.border-white\/5) {
  border-color: #e5e7eb !important;
}

/* BotMetrics, BotAiInsights panel chrome */
.br-content :deep(.rounded-xl) {
  border-radius: 8px !important;
}
.br-content :deep(.shadow) {
  box-shadow: none !important;
}
.br-content :deep(.outline-1) {
  outline-width: 1px !important;
}
.br-content :deep(.outline) {
  outline-style: solid !important;
}
.br-content :deep(.px-6) {
  padding-left: 16px !important;
  padding-right: 16px !important;
}
.br-content :deep(.py-5) {
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

/* Suppress all hover transform/scale/shadow effects on sub-elements */
.br-content :deep(*) {
  transition: none !important;
}
.br-content :deep(tr:hover),
.br-content :deep([class*='hover\:bg']:hover),
.br-content :deep([class*='hover\:shadow']:hover) {
  background: #fafafa !important;
  box-shadow: none !important;
  transform: none !important;
}

/* BotUnresolvedQuestions / BotImproveBacklog specific fixes */
.br-content :deep(.font-semibold) {
  font-weight: 600 !important;
}
.br-content :deep(.secondary-action-btn) {
  border-color: #d1d5db !important;
  color: #111827 !important;
}
.br-content :deep(.secondary-action-btn:hover) {
  background: #f9fafb !important;
}

/* Improve Backlog: row action buttons */
.br-content :deep(button[class*='border']) {
  border-color: #d1d5db;
  color: #111827;
}
.br-content :deep(select) {
  border-color: #d1d5db;
  background: #ffffff;
  color: #111827;
  border-radius: 6px;
  padding: 4px 8px;
}
.br-content :deep(input) {
  border-color: #d1d5db;
  background: #ffffff;
  color: #111827;
  border-radius: 6px;
  padding: 4px 8px;
}

/* ── Footer ── */
.br-footer {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

/* ── Dark mode overrides for shell ── */
::global(body.dark) .br-page,
::global(body.dark) .br-shell,
::global(body.dark) .br-header,
::global(body.dark) .br-toolbar,
::global(body.dark) .br-card,
::global(body.dark) .br-card-section,
::global(body.theme-mix) .br-page,
::global(body.theme-mix) .br-shell,
::global(body.theme-mix) .br-header,
::global(body.theme-mix) .br-toolbar,
::global(body.theme-mix) .br-card,
::global(body.theme-mix) .br-card-section {
  background: #ffffff !important;
  color: #111827 !important;
}

::global(body.dark) .br-title,
::global(body.dark) .br-card-label,
::global(body.dark) .br-card-value,
::global(body.theme-mix) .br-title,
::global(body.theme-mix) .br-card-label,
::global(body.theme-mix) .br-card-value {
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .br-content {
    padding: 12px 16px 16px;
  }
  .br-toolbar {
    padding: 10px 16px 8px;
  }
  .br-cards {
    grid-template-columns: 1fr;
  }
}
</style>
